import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Lock, Zap, Sparkles, ArrowRight, BookOpen } from 'lucide-react';
type UpsellPopupProps = {
  open: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onClose: () => void;
  // pricing (passed in from parent so single source of truth)
  trueTotal: number;
  bundlePrice: number;
  savings: number;
};
const formatUSD = (n: number) => `$${n.toFixed(2)}`;
export function UpsellPopup({
  open,
  onAccept,
  onDecline,
  onClose,
  trueTotal,
  bundlePrice,
  savings
}: UpsellPopupProps) {
  // Lock body scroll while open
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';else document.body.style.overflow = '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  // ESC closes
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);
  return <AnimatePresence data-id="element-347">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="upsell-title" data-id="element-348">
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-espresso/75 backdrop-blur-sm" onClick={onClose} data-id="element-349" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto border-2 border-caramel/30" data-id="element-350">
            {/* Close button */}
            <button onClick={onClose} aria-label="Cerrar" className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-cream-dark/40 flex items-center justify-center text-espresso transition-colors shadow-md" data-id="element-351">
              <X className="w-4 h-4" data-id="element-352" />
            </button>

            {/* HEADER BANNER */}
            <div className="bg-gradient-to-br from-caramel to-caramel-dark text-white px-6 pt-8 pb-7 rounded-t-3xl relative overflow-hidden" data-id="element-353">
              {/* Decorative pattern */}
              <div className="absolute inset-0 opacity-10 pointer-events-none" data-id="element-354">
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white" data-id="element-355" />
                <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-white" data-id="element-356" />
              </div>

              <div className="relative text-center" data-id="element-357">
                {/* Urgency badge (flashing) */}
                <div className="inline-flex items-center gap-1.5 bg-red-600 text-white px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider mb-4 animate-flash-badge shadow-lg" data-id="element-358">
                  <Zap className="w-3 h-3" data-id="element-359" />
                  ¡Espera! Última oportunidad
                </div>

                <h2 id="upsell-title" className="font-serif text-2xl sm:text-3xl font-bold leading-tight mb-2" data-id="element-360">
                  🛑 No te vayas con las manos a medias
                </h2>
                <p className="text-cream-dark text-sm sm:text-base font-medium opacity-95" data-id="element-361">
                  Te falta el secreto que multiplica tus pedidos
                </p>
              </div>
            </div>

            {/* BODY */}
            <div className="px-6 pt-6 pb-6" data-id="element-362">
              {/* Flashing discount badge */}
              <div className="flex justify-center mb-5" data-id="element-363">
                <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider animate-flash-badge border-2 border-red-300" data-id="element-364">
                  <Sparkles className="w-4 h-4" data-id="element-365" />
                  30% OFF en los extras
                </div>
              </div>

              {/* Main copy */}
              <p className="text-espresso text-base sm:text-lg leading-relaxed text-center mb-3 font-medium" data-id="element-366">
                Las recetas te enseñan a hornear. Los extras te enseñan a{' '}
                <span className="italic text-caramel" data-id="element-367">
                  vender y cobrar bien
                </span>
                .
              </p>
              <p className="text-espresso-light text-sm sm:text-base leading-relaxed text-center mb-6" data-id="element-368">
                Llévate el paquete completo y suma la Guía de Costos + el Pack
                de 10 Rellenos Gourmet con 30% de descuento — solo en esta
                pantalla.
              </p>

              {/* WHAT'S INCLUDED */}
              <div className="bg-cream-dark/30 border border-caramel/20 rounded-2xl p-4 mb-5" data-id="element-369">
                <p className="text-xs font-bold uppercase tracking-widest text-caramel mb-3 flex items-center gap-1.5" data-id="element-370">
                  <BookOpen className="w-3.5 h-3.5" data-id="element-371" />
                  Esto te llevas hoy
                </p>
                <ul className="space-y-2.5" data-id="element-372">
                  <li className="flex items-start gap-2.5" data-id="element-373">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-374">
                      <Check className="w-3 h-3 text-green-700" strokeWidth={3} data-id="element-375" />
                    </div>
                    <div className="text-sm leading-snug" data-id="element-376">
                      <span className="font-bold text-espresso" data-id="element-377">
                        Master en Repostería en Casa
                      </span>
                      <span className="text-espresso-light" data-id="element-378">
                        {' '}
                        — Manual completo, milhojas y galletas gourmet
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start gap-2.5" data-id="element-379">
                    <div className="w-5 h-5 rounded-full bg-caramel/20 flex items-center justify-center shrink-0 mt-0.5" data-id="element-380">
                      <Sparkles className="w-3 h-3 text-caramel" strokeWidth={3} data-id="element-381" />
                    </div>
                    <div className="text-sm leading-snug" data-id="element-382">
                      <span className="font-bold text-espresso" data-id="element-383">
                        Guía de Costos y Precios
                      </span>
                      <span className="text-espresso-light" data-id="element-384">
                        {' '}
                        — Cobra lo que realmente vales, sin perder un centavo
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start gap-2.5" data-id="element-385">
                    <div className="w-5 h-5 rounded-full bg-caramel/20 flex items-center justify-center shrink-0 mt-0.5" data-id="element-386">
                      <Sparkles className="w-3 h-3 text-caramel" strokeWidth={3} data-id="element-387" />
                    </div>
                    <div className="text-sm leading-snug" data-id="element-388">
                      <span className="font-bold text-espresso" data-id="element-389">
                        Pack de 10 Rellenos Gourmet
                      </span>
                      <span className="text-espresso-light" data-id="element-390">
                        {' '}
                        — Pistacho, frutos rojos, chocolate belga y 7 más
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start gap-2.5" data-id="element-391">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-392">
                      <Check className="w-3 h-3 text-green-700" strokeWidth={3} data-id="element-393" />
                    </div>
                    <div className="text-sm leading-snug" data-id="element-394">
                      <span className="font-bold text-espresso" data-id="element-395">
                        Garantía de 30 días
                      </span>
                      <span className="text-espresso-light" data-id="element-396">
                        {' '}
                        — 100% sin riesgo
                      </span>
                    </div>
                  </li>
                </ul>
              </div>

              {/* PRICING BLOCK */}
              <div className="text-center mb-5" data-id="element-397">
                <div className="flex items-center justify-center gap-2 text-sm text-espresso-light mb-1" data-id="element-398">
                  <span className="line-through font-medium" data-id="element-399">
                    Valor real: {formatUSD(trueTotal)} USD
                  </span>
                </div>
                <div className="flex items-baseline justify-center gap-1 mb-1" data-id="element-400">
                  <span className="text-2xl font-bold text-caramel" data-id="element-401">$</span>
                  <span className="text-5xl sm:text-6xl font-bold text-caramel tracking-tighter font-serif" data-id="element-402">
                    {bundlePrice.toFixed(2)}
                  </span>
                  <span className="text-base font-bold text-caramel" data-id="element-403">USD</span>
                </div>
                <div className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs font-bold mb-2" data-id="element-404">
                  <Lock className="w-3 h-3" data-id="element-405" />
                  Precio especial solo en esta pantalla · Ahorra{' '}
                  {formatUSD(savings)}
                </div>
              </div>

              {/* CTA BUTTONS */}
              <button type="button" onClick={onAccept} className="group relative w-full bg-caramel hover:bg-caramel-dark text-white font-bold text-base sm:text-lg rounded-2xl px-5 py-4 shadow-glow transition-all hover:-translate-y-0.5 animate-cta-pulse overflow-hidden" data-id="element-406">
                <span className="absolute inset-0 w-full h-full bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" data-id="element-407" />
                <span className="relative flex items-center justify-center gap-2" data-id="element-408">
                  ¡SÍ! Quiero el paquete completo por {formatUSD(bundlePrice)}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-409" />
                </span>
              </button>

              <button type="button" onClick={onDecline} className="w-full mt-3 text-espresso-light hover:text-espresso text-sm font-medium py-2 transition-colors underline-offset-4 hover:underline" data-id="element-410">
                No gracias, continuar sin los extras →
              </button>

              {/* Trust line */}
              <div className="mt-4 pt-4 border-t border-caramel/10 flex items-center justify-center gap-1.5 text-xs text-espresso-light" data-id="element-411">
                <Lock className="w-3 h-3 text-green-600" data-id="element-412" />
                <span data-id="element-413">Pago 100% seguro · Acceso inmediato</span>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}